/* Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { When } from "react-if";
import styled from "styled-components";
import { FlexContainer, Input, Text, theme } from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { isEmpty, isNotEmpty } from "~/tools";

type Props = {
  description: string;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value?: string) => void;
  onDateToChange: (value?: string) => void;
  disabled?: boolean;
};

const today = dayjs().format(dateFormat.shortDate);

export function EstateDateRangeFilter({
  description,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  disabled = false,
}: Props) {
  const from = formatToShortDate(dateFrom);
  const to = formatToShortDate(dateTo);
  return (
    <>
      <When condition={isNotEmpty(description)}>
        <Text>{description}</Text>
      </When>
      <div css={{ whiteSpace: "nowrap" }}>
        <div css={{ display: "inline-block", width: "100%" }}>
          <FlexContainer>
            <DateInputContainer>
              <Input
                type="date"
                value={from}
                placeholder="MM/DD/YYYY"
                max={today}
                disabled={disabled}
                onChange={(event) =>
                  onDateFromChange(formatToIsoString(event.target.value))
                }
              />
            </DateInputContainer>
            <DateInputContainer>
              <Input
                type="date"
                value={to}
                placeholder="MM/DD/YYYY"
                max={today}
                disabled={isEmpty(from) || disabled}
                min={getMinToDate(from)}
                onChange={(event) =>
                  onDateToChange(formatToIsoString(event.target.value))
                }
              />
            </DateInputContainer>
          </FlexContainer>
        </div>
      </div>
    </>
  );
}

const DateInputContainer = styled.div`
  margin-right: ${theme.spacing.spacing02};
  width: 50%;
`;

function getMinToDate(date: string) {
  return formatToShortDate(date) || undefined;
}

function formatToIsoString(value: string) {
  return isNotEmpty(value)
    ? dayjs(value).startOf("day").toISOString()
    : undefined;
}

function formatToShortDate(date: string) {
  return isNotEmpty(date) ? dayjs(date).format(dateFormat.shortDate) : "";
}
